import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../config/supabaseClient';

interface UseMakesByCategoryResult {
  makeIds: Set<number>;
  loading: boolean;
}

type CategoryInput = number | number[] | null;

function normalizeIds(input: CategoryInput): number[] | null {
  if (input == null) return null;
  if (Array.isArray(input)) {
    const filtered = input.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    return filtered.length > 0 ? filtered : null;
  }
  return [input];
}

export function useMakesByCategory(categoryIds: CategoryInput): UseMakesByCategoryResult {
  const [makeIds, setMakeIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);

  const ids = useMemo(() => normalizeIds(categoryIds), [categoryIds]);
  const idsKey = ids ? ids.slice().sort((a, b) => a - b).join(',') : '';

  useEffect(() => {
    if (!ids) {
      setMakeIds(new Set());
      return;
    }

    const fetchMakeIds = async () => {
      setLoading(true);

      try {
        const { data, error } = await supabase
          .from('machines')
          .select('make_id')
          .in('category_id', ids);

        if (error) throw error;

        const next = new Set(
          (data || [])
            .map((m) => m.make_id)
            .filter((id): id is number => id != null),
        );
        setMakeIds(next);
      } catch (err: any) {
        console.error('Error fetching makes for category:', err);
        setMakeIds(new Set());
      } finally {
        setLoading(false);
      }
    };

    fetchMakeIds();
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { makeIds, loading };
}
